<template>
  <div class="cr-summary tr-summary" :class="{'is-loading': loading}">
    <div class="cr-summary-item cr-summary-scope text-left">
      <small>
        {{ mode === 'period' ? 'Período' : 'Competência' }}
        <span class="tooltip tooltip-bottom tr-scope-help" :data-tooltip="scopeHelp">
          <fa-icon :icon="['fal', 'info-circle']"/>
        </span>
      </small>
      <div class="cr-summary-value">{{ periodLabel }}</div>
      <span class="cr-summary-hint">{{ professionalsHint }}</span>
    </div>

    <div class="cr-summary-item pending" :class="{zero: !totals.pending}" v-if="showPending">
      <small>Pendente de consolidar</small>
      <div class="cr-summary-value tr-num">{{ currency(totals.pending) }}</div>
      <span class="cr-summary-hint">
        {{ totals.pendingCount }} {{ pluralize(totals.pendingCount, 'item', 'itens') }}
      </span>
    </div>

    <div class="cr-summary-item highlight" :class="{zero: !totals.consolidated}"
         v-if="showConsolidated">
      <small>Consolidado a pagar</small>
      <div class="cr-summary-value tr-num">{{ currency(totals.consolidated) }}</div>
      <span class="cr-summary-hint">
        {{ consolidatedCount }}
        {{ pluralize(consolidatedCount, 'conta a pagar', 'contas a pagar') }}
      </span>
    </div>

    <div class="cr-summary-item done soft" :class="{zero: !totals.paid}" v-if="showPaid">
      <small>Pago</small>
      <div class="cr-summary-value tr-num">{{ currency(totals.paid) }}</div>
      <span class="cr-summary-hint">
        {{ paidCount }} {{ pluralize(paidCount, 'conta', 'contas') }}
      </span>
    </div>

    <div class="cr-summary-item soft" v-if="hasAdjustments">
      <small>Ajustes pendentes</small>
      <div class="cr-summary-value tr-num" :class="adjustmentsClass">
        {{ signedCurrency(totals.adjustments) }}
      </div>
      <span class="cr-summary-hint">{{ adjustmentsHint }}</span>
    </div>

    <div class="cr-summary-item soft" :class="{zero: !origin.total}"
         v-for="origin in origins" :key="origin.key">
      <small><fa-icon :icon="['fal', origin.icon]"/>{{ origin.label }}</small>
      <div class="cr-summary-value tr-num">{{ origin.total | currency }}</div>
      <span class="cr-summary-hint">{{ origin.hint }}</span>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { shortNames, icons } from '@/data/billing-transfer-origins';
import {
  competenceLabel, currency, pluralize, signedCurrency,
} from '../helpers';

const SCOPE_HELP = 'Lotes entram pela competência do lote; caixa e laudos, pela data do '
  + 'atendimento.';

const EMPTY = {
  pending: 0,
  pendingCount: 0,
  consolidated: 0,
  paid: 0,
  adjustments: 0,
  adjustmentsCount: { debit: 0, credit: 0 },
  byOrigin: { lot: 0, cashRegister: 0, medicalReport: 0 },
  professionals: 0,
};

export default {
  props: {
    totals: {
      type: Object,
      default: () => ({ ...EMPTY }),
    },
    counts: {
      type: Object,
      default: () => ({
        consolidated: 0, paid: 0, lot: 0, cashRegister: 0, medicalReport: 0,
      }),
    },
    period: {
      type: Object,
      default: null,
    },
    mode: {
      type: String,
      default: 'competence',
    },
    statuses: {
      type: Array,
      default: () => [],
    },
    originFilter: {
      type: String,
      default: 'all',
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    scopeHelp() {
      return SCOPE_HELP;
    },
    periodLabel() {
      if (!this.period) return '—';
      if (this.mode === 'period' || !this.period.competence) {
        const start = moment(this.period.startDate).format('L');
        const end = moment(this.period.endDate).format('L');
        return `${start} a ${end}`;
      }
      return competenceLabel(moment(this.period.competence, 'YYYY-MM').format('MM/YYYY'));
    },
    professionalsHint() {
      const total = this.totals.professionals || 0;
      return `${total} ${pluralize(total, 'profissional', 'profissionais')}`;
    },
    showPending() {
      return this.statuses.includes('pending');
    },
    showConsolidated() {
      return this.statuses.includes('consolidated');
    },
    showPaid() {
      return this.statuses.includes('paid');
    },
    consolidatedCount() {
      return this.counts.consolidated || 0;
    },
    paidCount() {
      return this.counts.paid || 0;
    },
    hasAdjustments() {
      const count = this.totals.adjustmentsCount || EMPTY.adjustmentsCount;
      return !!(this.totals.adjustments || count.debit || count.credit);
    },
    adjustmentsClass() {
      const value = Number(this.totals.adjustments) || 0;
      if (value < 0) return 'text-error';
      if (value > 0) return 'text-green';
      return 'text-muted';
    },
    adjustmentsHint() {
      const count = this.totals.adjustmentsCount || EMPTY.adjustmentsCount;
      const parts = [];
      if (count.debit) parts.push(`${count.debit} ${pluralize(count.debit, 'débito', 'débitos')}`);
      if (count.credit) {
        parts.push(`${count.credit} ${pluralize(count.credit, 'crédito', 'créditos')}`);
      }
      return parts.length > 0 ? parts.join(' · ') : 'nenhum ajuste';
    },
    origins() {
      const byOrigin = this.totals.byOrigin || EMPTY.byOrigin;
      const keys = ['lot', 'cashRegister', 'medicalReport']
        .filter(key => this.originFilter === 'all' || this.originFilter === key);
      return keys.map((key) => {
        const count = this.counts[key] || 0;
        let noun = pluralize(count, 'item', 'itens');
        if (key === 'lot') noun = pluralize(count, 'lote', 'lotes');
        else if (key === 'medicalReport') noun = pluralize(count, 'laudo', 'laudos');
        return {
          key,
          label: shortNames[key],
          icon: icons[key],
          total: byOrigin[key] || 0,
          hint: `${count} ${noun}`,
        };
      });
    },
  },
  methods: {
    currency,
    pluralize,
    signedCurrency,
  },
};
</script>
